import { Card, CardContent } from '@/shared/ui/card';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { toast } from '@/shared/hooks/use-toast';
import { PhotoMarker } from './types/types';
import { useEffect, useRef } from 'react';

interface PhotoNavigationProps {
  markers: PhotoMarker[];
  currentPhotoId: number;
  onNavigateToPhoto: (photoId: number) => void;
}

const PhotoNavigation: React.FC<PhotoNavigationProps> = ({
  markers,
  currentPhotoId,
  onNavigateToPhoto,
}) => {
  const desktopCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const positionBelowLegend = () => {
      const legend = document.querySelector('[data-testid="hotspot-legend"]') as HTMLElement;
      const photoNav = desktopCardRef.current;

      if (legend && photoNav) {
        const legendHeight = legend.offsetHeight;
        const legendTop = legend.offsetTop;
        const newTop = legendTop + legendHeight + 10; // 10px below legend

        photoNav.style.top = `${newTop}px`;
      }
    };

    // Position initially with a delay
    const timer = setTimeout(positionBelowLegend, 100);

    // Set up ResizeObserver to watch legend for height changes
    const legend = document.querySelector('[data-testid="hotspot-legend"]') as HTMLElement;
    let resizeObserver: ResizeObserver | null = null;

    if (legend) {
      resizeObserver = new ResizeObserver(() => {
        // Reposition whenever legend size changes
        positionBelowLegend();
      });

      resizeObserver.observe(legend);
    }

    // Also set up a MutationObserver to catch content changes
    let mutationObserver: MutationObserver | null = null;

    if (legend) {
      mutationObserver = new MutationObserver(() => {
        // Small delay to let content render before repositioning
        setTimeout(positionBelowLegend, 50);
      });

      mutationObserver.observe(legend, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
      });
    }

    return () => {
      clearTimeout(timer);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (mutationObserver) {
        mutationObserver.disconnect();
      }
    };
  }, [currentPhotoId]); // Still re-setup observers when photo changes

  // Find the current marker that contains the current photo
  const getCurrentMarker = () => {
    return markers.find((marker) => marker.photos.some((photo) => photo.id === currentPhotoId));
  };

  // Find next marker (current index + 1)
  const getNextMarker = () => {
    const currentMarker = getCurrentMarker();
    if (!currentMarker) return null;

    return markers.find((marker) => marker.number === currentMarker.number + 1);
  };

  // Find previous marker (current index - 1)
  const getPrevMarker = () => {
    const currentMarker = getCurrentMarker();
    if (!currentMarker) return null;

    return markers.find((marker) => marker.number === currentMarker.number - 1);
  };

  const handlePrevClick = () => {
    const prevMarker = getPrevMarker();
    if (prevMarker && prevMarker.photos.length > 0) {
      onNavigateToPhoto(prevMarker.photos[0].id);
    } else {
      toast({
        title: 'Navigation',
        description: 'No previous marker available',
      });
    }
  };

  const handleNextClick = () => {
    const nextMarker = getNextMarker();
    if (nextMarker && nextMarker.photos.length > 0) {
      onNavigateToPhoto(nextMarker.photos[0].id);
    } else {
      toast({
        title: 'Navigation',
        description: 'No next marker available',
      });
    }
  };

  const prevMarker = getPrevMarker();
  const nextMarker = getNextMarker();

  return (
    <>
      {/* Mobile Layout - Top center at same height as X button */}
      <Card
        className="absolute z-10 bg-gray-500 border-2 border-white
                   top-4 left-1/2 transform -translate-x-1/2 w-20 md:hidden"
        data-testid="photo-navigation-mobile"
      >
        <CardContent className="p-2 rounded-lg">
          <div className="flex items-center justify-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-white hover:bg-white/20 disabled:opacity-50 disabled:hover:bg-transparent"
              onClick={handlePrevClick}
              disabled={!prevMarker}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-white hover:bg-white/20 disabled:opacity-50 disabled:hover:bg-transparent"
              onClick={handleNextClick}
              disabled={!nextMarker}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Desktop/Tablet Layout - Positioned 10px below legend */}
      <Card
        ref={desktopCardRef}
        className="absolute z-10 bg-gray-500 border-2 border-white
                   hidden md:block right-4 w-[145px]"
        style={{ top: '350px' }} // Fallback position
        data-testid="photo-navigation-desktop"
      >
        <CardContent className="p-3 rounded-lg">
          <div className="flex items-center justify-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-white hover:bg-white/20 disabled:opacity-50 disabled:hover:bg-transparent"
              onClick={handlePrevClick}
              disabled={!prevMarker}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-white text-xs font-medium">Photos</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-white hover:bg-white/20 disabled:opacity-50 disabled:hover:bg-transparent"
              onClick={handleNextClick}
              disabled={!nextMarker}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
};

export default PhotoNavigation;
